import React from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';

import { CORPORATE_STRUCTURE_OPTIONS, FEIN_MASK, INDUSTRIES_OPTIONS } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import { DefaultDisclaimerInputSection } from 'src/components/disclaimer';
import {
  AddressFields,
  AutocompleteField,
  AvatarImageUploadField,
  Field,
  Form,
  MaskedTextField,
  NumberField,
  PhoneField,
  SelectField,
  TextField,
} from 'src/components/finalForm';
import { TAX_YEAR_END_DATE_MASK } from 'src/constants/masks';
import { useAuthSignup } from 'src/hooks/auth';
import type { SxProp } from 'src/types/mui';
import { getUserFullName } from 'src/utils/user';
import { validateWithYupSchema } from 'src/utils/yup';

import {
  PEOPayrollProviderBasicInfoFormFields,
  PEOPayrollProviderBasicInfoFormValues,
} from '../types';
import { peoPayrollProviderBasicInfoFormValidationSchema } from '../utils';

export const PEOPayrollProviderBasicInfoForm: React.FC = () => {
  const { formData, inviteData, updateFormData, goToNextStep } = useAuthSignup();

  const handleSubmit = React.useCallback(
    (formValues: PEOPayrollProviderBasicInfoFormValues) => {
      updateFormData(formValues);
      goToNextStep();
    },
    [goToNextStep, updateFormData],
  );

  return (
    <Box>
      <Typography variant="h5">Basic info</Typography>
      <Typography variant="body1" fontSize="16px" mt={1}>
        Hello <b>{getUserFullName(formData)}</b>! Let us know more information about your company.
      </Typography>
      <Form<PEOPayrollProviderBasicInfoFormValues>
        onSubmit={handleSubmit}
        subscription={{ valid: true, submitting: true }}
        containerProps={{ mt: 4 }}
        validate={validateWithYupSchema(peoPayrollProviderBasicInfoFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Grid container spacing={1.5}>
                <Grid
                  size={{
                    xs: 12,
                    md: 8,
                  }}
                >
                  <Box>
                    <Field
                      component={TextField}
                      name={PEOPayrollProviderBasicInfoFormFields.displayName}
                      label="Company Display Name"
                      fullWidth
                      disabled={submitting}
                      required
                    />
                  </Box>

                  <Box mt={1.5}>
                    <Field
                      label="Corporate Structure"
                      name={PEOPayrollProviderBasicInfoFormFields.corporateStructure}
                      fullWidth
                      disabled={submitting}
                      options={CORPORATE_STRUCTURE_OPTIONS}
                      component={SelectField}
                    />
                  </Box>
                </Grid>

                <Grid
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <Box sx={avatarStyles}>
                    <Field
                      name={PEOPayrollProviderBasicInfoFormFields.avatarImageFile}
                      component={AvatarImageUploadField}
                      defaultImageUrl={DEFAULT_AVATAR}
                      avatarProps={{ sx: avatarStyles }}
                      uploadInfoQueryVariables={{ inviteId: inviteData?.id }}
                      disabled={submitting}
                    />
                  </Box>
                </Grid>

                <Grid size={12}>
                  <Field
                    component={AutocompleteField}
                    name={PEOPayrollProviderBasicInfoFormFields.industry}
                    label="Industry"
                    options={INDUSTRIES_OPTIONS}
                    fullWidth
                    disabled={submitting}
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    fullWidth
                    label="Tax Year End Date"
                    name={PEOPayrollProviderBasicInfoFormFields.taxYearEndDate}
                    component={MaskedTextField}
                    inputProps={{
                      placeholder: 'MM/DD',
                    }}
                    mask={TAX_YEAR_END_DATE_MASK}
                    disabled={submitting}
                  />
                </Grid>

                <Grid size={6}>
                  <Field
                    label="Number of Employees"
                    name={PEOPayrollProviderBasicInfoFormFields.numberOfEmployees}
                    fullWidth
                    disabled={submitting}
                    component={NumberField}
                  />
                </Grid>

                <Grid size={12}>
                  <Field
                    component={MaskedTextField}
                    name={PEOPayrollProviderBasicInfoFormFields.fein}
                    label="FEIN"
                    mask={FEIN_MASK}
                    fullWidth
                    disabled={submitting}
                    required
                  />
                </Grid>

                <Grid size={12}>
                  <Field
                    component={TextField}
                    name={PEOPayrollProviderBasicInfoFormFields.contactEmail}
                    label="Primary Contact Email"
                    fullWidth
                    disabled={submitting}
                    required
                  />
                </Grid>

                <Grid size={12}>
                  <Field
                    component={PhoneField}
                    name={PEOPayrollProviderBasicInfoFormFields.contactPhoneNumber}
                    label="Primary Contact Phone"
                    fullWidth
                    disabled={submitting}
                  />
                </Grid>

                <Grid spacing={2}>
                  <Grid size={12}>
                    <Typography fontWeight={600}>Headquarters address</Typography>
                  </Grid>
                  <Grid mt={2}>
                    <AddressFields
                      prefix={PEOPayrollProviderBasicInfoFormFields.headquarters}
                      requiredStreet
                      requiredCity
                      requiredState
                      requiredZipCode
                      disabled={submitting}
                      addressLabel="Street"
                    />
                  </Grid>
                </Grid>

                <Grid size={12}>
                  <DefaultDisclaimerInputSection
                    headerText="PEO Payroll Provider Disclaimer"
                    name={PEOPayrollProviderBasicInfoFormFields.disclaimer}
                  />
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="center" mt={4}>
                <Button
                  type="submit"
                  sx={submitButtonStyles}
                  disabled={submitting || !valid}
                  loading={submitting}
                  size="large"
                  variant="contained"
                >
                  Next step
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};

const submitButtonStyles: SxProp = {
  px: 5,
};

const avatarStyles: SxProp = {
  width: 120,
  height: 120,
};
